<template>
  <div>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <v-dialog v-model="dialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-4">Add Announcement</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6">
          <v-form ref="form" >
            <v-row dense>
              <v-col cols="12">
                <div class="mb-4">
                  <label class="v-label text-body-2 mb-2 d-block">
                    Announcement Visibility <span class="text-error">*</span>
                  </label>
                  <v-radio-group v-model="formData.announcement_visiblity" inline>
                    <v-radio
                      label="All"
                      value="ALL"
                    ></v-radio>
                    <v-radio
                      label="Organization"
                      value="ORGANIZATION"
                    ></v-radio>
                  </v-radio-group>
                </div>
              </v-col>
              <v-col cols="12" v-if="formData.announcement_visiblity === 'ORGANIZATION'">
                <v-autocomplete 
                  v-model="formData.visible_for"
                  :items="organizationOptions"
                  item-title="title"
                  item-value="value"
                  label="Select Organizations"
                  multiple
                  variant="outlined"
                  density="compact"
                  chips
                  closable-chips
                  :rules="[
                    (v) => 
                      formData.announcement_visiblity !== 'ORGANIZATION' || 
                      (v && v.length > 0) || 
                      'Please select at least one organization'
                  ]"
                >
                  <template v-slot:prepend-item>
                    <v-list-item
                      title="Select All"
                      @click="toggleSelectAll"
                    >
                      <template v-slot:prepend>
                        <v-checkbox
                          :model-value="isAllSelected"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                      </template>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12">
                <div class="rich-text-editor">
                  <label class="v-label text-body-2 mb-2 d-block">
                    Announcement Message <span class="text-error">*</span>
                  </label>
                  <v-card variant="outlined" class="editor-container">
                    <div class="editor-toolbar">
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        :class="{ 'active': activeFormats.bold }"
                        :color="activeFormats.bold ? 'primary' : undefined"
                        @click="formatText('bold')"
                        title="Bold"
                      >
                        <v-icon>mdi-format-bold</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        :class="{ 'active': activeFormats.italic }"
                        :color="activeFormats.italic ? 'primary' : undefined"
                        @click="formatText('italic')"
                        title="Italic"
                      >
                        <v-icon>mdi-format-italic</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        :class="{ 'active': activeFormats.underline }"
                        :color="activeFormats.underline ? 'primary' : undefined"
                        @click="formatText('underline')"
                        title="Underline"
                      >
                        <v-icon>mdi-format-underline</v-icon>
                      </v-btn>
                    </div>
                    <v-divider></v-divider>
                    <div
                      ref="editorContent"
                      contenteditable="true"
                      class="editor-content"
                      :class="{ 'error': messageError }"
                      placeholder="Enter announcement message..."
                      @input="handleEditorInput"
                      @focus="updateToolbarState"
                      @keyup="updateToolbarState"
                      @keydown="updateToolbarState"
                      @mouseup="updateToolbarState"
                      @click="updateToolbarState"
                    ></div>
                  </v-card>
                  <div v-if="messageError" class="text-error text-caption mt-1">
                    {{ messageError }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="handleClose"
            :disabled="loading"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="loading"
            :loading="loading"
            @click="handleSubmit"
          >
            Create Announcement
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import { create_announcement } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";
import GetOrganizations from "@/mixins/Organization/GetOrganizations";

const client = generateClient();

export default {
  name: "CreateAnnouncementDialog",
  mixins: [GetOrganizations],
  components: {
    SnackbarComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "created"],
  data() {
    return {
      loading: false,
      formData: {
        announcement_message: "",
        announcement_visiblity: "ALL",
        visible_for: [],
      },
      activeFormats: {
        bold: false,
        italic: false,
        underline: false,
      },
      isUpdatingEditor: false,
      messageError: "",
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    organizationOptions() {
      return (this.organizationsList || []).map((org) => ({
        title: org.organization_name || org.organizationName || "",
        value: org.organization_id || org.id || "",
      }));
    },
    isAllSelected() {
      if (!this.organizationsList || this.organizationsList.length === 0) {
        return false;
      }
      const allValues = this.organizationOptions.map((opt) => opt.value);
      return (
        this.formData.visible_for.length === allValues.length &&
        allValues.every((val) => this.formData.visible_for.includes(val))
      );
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.fetchOrganizations();
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    handleClose() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        announcement_message: "",
        announcement_visiblity: "ALL",
        visible_for: [],
      };
      if (this.$refs.editorContent) {
        this.$refs.editorContent.innerHTML = "";
      }
      this.messageError = "";
      this.activeFormats = {
        bold: false,
        italic: false,
        underline: false,
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    formatText(command) {
      if (!this.$refs.editorContent) return;
      
      // Focus editor if not already focused
      if (document.activeElement !== this.$refs.editorContent) {
        this.$refs.editorContent.focus();
      }
      
      // Execute the formatting command
      document.execCommand(command, false, null);
      
      // Immediately update toolbar state after formatting
      this.$nextTick(() => {
        this.updateToolbarState();
        
        // Update the model value after formatting
        if (this.$refs.editorContent) {
          this.formData.announcement_message = this.$refs.editorContent.innerHTML;
          this.handleEditorInput();
        }
      });
    },
    updateToolbarState() {
      this.$nextTick(() => {
        if (!this.$refs.editorContent) return;
        
        const hasFocus = document.activeElement === this.$refs.editorContent;
        const selection = window.getSelection();
        const hasSelection = selection && selection.toString().length > 0;
        
        // Only update if editor has focus
        if (!hasFocus) {
          return;
        }
        
        try {
          // Use queryCommandState to check current formatting
          this.activeFormats.bold = document.queryCommandState('bold');
          this.activeFormats.italic = document.queryCommandState('italic');
          this.activeFormats.underline = document.queryCommandState('underline');
        } catch (e) {
          // Fallback: check formatting by examining the selection
          if (hasSelection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const container = range.commonAncestorContainer;
            const element = container.nodeType === 3 ? container.parentElement : container;
            
            if (element) {
              this.activeFormats.bold = element.tagName === 'B' || element.tagName === 'STRONG' || 
                                        window.getComputedStyle(element).fontWeight >= 600 ||
                                        element.closest('b, strong') !== null;
              this.activeFormats.italic = element.tagName === 'I' || element.tagName === 'EM' ||
                                         window.getComputedStyle(element).fontStyle === 'italic' ||
                                         element.closest('i, em') !== null;
              this.activeFormats.underline = element.tagName === 'U' ||
                                             window.getComputedStyle(element).textDecoration.includes('underline') ||
                                             element.closest('u') !== null;
            }
          } else {
            // No selection, reset states
            this.activeFormats.bold = false;
            this.activeFormats.italic = false;
            this.activeFormats.underline = false;
          }
        }
      });
    },
    validateEditorContent() {
      if (!this.$refs.editorContent) {
        this.messageError = "Announcement message is required";
        return false;
      }
      
      // Get text content, removing HTML tags and entities
      let textContent = this.$refs.editorContent.textContent || this.$refs.editorContent.innerText || '';
      
      // Also check innerHTML for cases where textContent might not work
      if (!textContent.trim() && this.$refs.editorContent.innerHTML) {
        // Create a temporary element to extract text
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.$refs.editorContent.innerHTML;
        textContent = tempDiv.textContent || tempDiv.innerText || '';
      }
      
      // Remove whitespace and check if there's actual content
      const trimmedContent = textContent.trim();
      
      if (!trimmedContent) {
        this.messageError = "Announcement message is required";
        return false;
      } else {
        this.messageError = "";
        return true;
      }
    },
    handleEditorInput() {
      if (this.$refs.editorContent && !this.isUpdatingEditor) {
        const content = this.$refs.editorContent.innerHTML;
        this.isUpdatingEditor = true;
        this.formData.announcement_message = content;
        this.$nextTick(() => {
          this.isUpdatingEditor = false;
        });
        
        // Validate
        this.validateEditorContent();
      }
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.formData.visible_for = [];
      } else {
        this.formData.visible_for = this.organizationOptions.map((opt) => opt.value);
      }
    },
    async handleSubmit() {
      if (!this.validateEditorContent()) {
        return;
      }

      // Validate visibility and organizations
      if (this.formData.announcement_visiblity === "ORGANIZATION") {
        if (!this.formData.visible_for || this.formData.visible_for.length === 0) {
          this.snackbar = {
            show: true,
            message: "Please select at least one organization",
            color: "error",
          };
          return;
        }
      }

      this.loading = true;

      try {
        const input = {
          announcement_message: this.formData.announcement_message,
          announcement_source: "BMS",
          announcement_status: "PUBLISH",
          announcement_visiblity: this.formData.announcement_visiblity,
        };

        // Add visible_for only if visibility is ORGANIZATION
        if (this.formData.announcement_visiblity === "ORGANIZATION") {
          input.visible_for = this.formData.visible_for;
          
          // Optionally add visible_for_name (organization names)
          const selectedOptions = this.organizationOptions.filter(
            (opt) => this.formData.visible_for.includes(opt.value)
          );
          input.visible_for_name = selectedOptions.map((opt) => opt.title);
        }

        const response = await client.graphql({
          query: create_announcement,
          variables: {
            input: input,
          },
        });

        const responseData = JSON.parse(response.data.create_announcement);

        if (responseData.status === "SUCCESS") {
          this.snackbar = {
            show: true,
            message: responseData.status_message || "Announcement created successfully!",
            color: "success",
          };
          this.$emit("created");
          this.handleClose();
        } else {
          this.snackbar = {
            show: true,
            message: responseData.status_message || "Failed to create announcement",
            color: "error",
          };
        }
      } catch (error) {
        console.error("Error creating announcement:", error);
        this.snackbar = {
          show: true,
          message:
            (error.errors && error.errors[0]?.message) ||
            error.message ||
            "An error occurred while creating announcement",
          color: "error",
        };
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.rich-text-editor {
  width: 100%;
}

.editor-container {
  border-radius: 4px;
}

.editor-toolbar {
  display: flex;
  gap: 4px;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.editor-toolbar .v-btn {
  min-width: 32px;
  width: 32px;
  height: 32px;
}

.editor-toolbar .v-btn.active,
.editor-toolbar .v-btn[color="primary"] {
  background-color: rgba(var(--v-theme-primary), 0.2) !important;
  color: rgb(var(--v-theme-primary)) !important;
  border: 1px solid rgba(var(--v-theme-primary), 0.3) !important;
}

.editor-toolbar .v-btn.active .v-icon,
.editor-toolbar .v-btn[color="primary"] .v-icon {
  color: rgb(var(--v-theme-primary)) !important;
}

.editor-content {
  min-height: 120px;
  max-height: 300px;
  overflow-y: auto;
  padding: 12px;
  outline: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.87);
}

.editor-content:empty:before {
  content: attr(placeholder);
  color: rgba(0, 0, 0, 0.38);
  pointer-events: none;
}

.editor-content:focus {
  outline: none;
}

.editor-content.error {
  border-color: rgb(var(--v-theme-error));
}
</style>
